import type { components } from '../../lib/api/schema.gen'
import type { ConfigSchema } from '../catalog/configurator-sku'

type Sourceable = components['schemas']['Sourceable']

/**
 * UID-265b (ADR-0093) — pure, framework-free helpers for the post-submit
 * "Add parts to this RFQ" flow: the four-way 409 classification the append
 * contract defines, and the candidate list + client-side search the design's
 * modal (`Fictiv Platform.dc.html` L3630-3666) needs. Kept out of the
 * component so the branch-per-conflict behaviour is unit-testable without
 * mounting React, matching `review-domain.ts`'s own split.
 */

/** One row in the Add-parts modal's candidate list. `tag` is the design's
 * per-row badge (L3646: `{{ ap.tag }}`) — "Buy" for a global catalog
 * part, "Library" for a tenant-owned custom/library part. */
export interface AddPartsCandidateRow {
  readonly sourceable: Sourceable
  readonly name: string
  readonly partNumber: string | null
  readonly tag: 'Buy' | 'Library'
  /**
   * The catalog-search result-card's `configSchema` (UID-457 follow-up,
   * inline-configure) — lives on `CatalogResultCard`, NOT on the shared
   * `Sourceable` itself (same ADR-0030/0090 projection-boundary reason as
   * `category`; see `configurator-sku.ts`'s own doc comment), so it must be
   * threaded through from the catalog-search envelope explicitly rather than
   * read off `sourceable`. Undefined for a library row (`GET /parts` has no
   * `CatalogResultCard` envelope to carry one) and for a plain catalog row
   * with no configurable family — never fabricated. A `kind: 'configurable'`
   * candidate with this UNDEFINED (or gated `disabled` by
   * `gateConfigSchema`) falls back to the honest "configure in the catalog"
   * disabled treatment; `AddPartsToRfqModal.tsx` is the one place that
   * branches on it.
   */
  readonly configSchema?: ConfigSchema
}

/**
 * The single mapper from a `Sourceable` + its known tag to an
 * `AddPartsCandidateRow` — used by `add-parts-search.ts`'s live producer
 * (`searchAddPartsCandidates`, ADR-0147 D1), so the `name`/`partNumber`
 * derivation has exactly one definition. `tag` is always the CALLER's
 * branch label, never inferred from the `sourceable` itself (ADR-0147 D1 —
 * the tag is derived from which read produced the row, not sniffed from
 * data, even though the `accountId` presence/absence invariant would make
 * that technically possible).
 *
 * UID-457 item 2 (ADR-0147 D3) — this file used to ALSO export a fixture
 * producer (`addPartsCandidates`) and its client-side filter
 * (`filterAddPartsCandidates`), the post-submit `AddPartsToRfqModal`'s
 * candidate source before it was swapped onto `searchAddPartsCandidates`.
 * Both are deleted now that both add-parts surfaces share the one real
 * search — see ADR-0093 D9/amendment and ADR-0147 D3.
 */
export function toAddPartsCandidateRow(
  sourceable: Sourceable,
  tag: AddPartsCandidateRow['tag'],
  configSchema?: ConfigSchema,
): AddPartsCandidateRow {
  return {
    sourceable,
    name: sourceable.name ?? `Item ${sourceable.id.slice(0, 8)}`,
    partNumber: sourceable.catalogSku ?? null,
    tag,
    ...(configSchema !== undefined ? { configSchema } : {}),
  }
}

/**
 * The append endpoint's four documented 409 causes (ADR-0093 D1/D4,
 * `appendRfqLines`'s `409` description in `schema.gen.ts`).
 *
 * `unknown` is a real member, not a defensive afterthought: `Problem` carries
 * no machine-readable conflict key on the wire — only human `detail` prose
 * from `CONFLICT_DETAIL` (`apps/api/src/modules/rfq/routes.ts:58-71`) — so a
 * detail this client does not recognize MUST fall through to the generic
 * "refetch and show the server's message" branch rather than be guessed into
 * one of the four (guessing `stale` would silently retry a write).
 */
export type AppendLinesConflict =
  | 'line-already-present'
  | 'quote-in-progress'
  | 'lines-not-appendable'
  | 'stale'
  /** UID-265b defense-in-depth follow-up — the server-side counterpart to
   * this modal's own `kind === 'configurable'` gate (`AddPartsToRfqModal.tsx`
   * / `AddPartsModal.tsx`): a `kind: 'configurable'` Sourceable reached the
   * append endpoint anyway (the frontend gate should make this rare — a
   * race, a stale client, or a caller that bypasses the modal) and the
   * server refused it (`apps/api`'s `RfqLineAppendConflict['line-not-configured']`). */
  | 'line-not-configured'
  | 'unknown'

/**
 * Distinctive, stable substrings of each server `detail`. Matched on a fragment
 * rather than by full-string equality so a copy tweak on the server degrades to
 * `unknown` (generic handling + the server's own message) instead of misrouting
 * a branch.
 *
 * ⚠ COUPLED SOURCE — `CONFLICT_DETAIL` in `apps/api/src/modules/rfq/routes.ts`
 * (which carries the reciprocal pointer back here). These are two copies of one
 * vocabulary with NOTHING in the type system linking them: this file matches
 * prose the server owns, so a server copy edit or an i18n pass changes THIS
 * client's control flow (`stale` → retries the write, `quote-in-progress` →
 * closes the modal). Edit one, edit the other.
 *
 * The link is held only by a test — `rfq-append-lines.test.ts`'s D10 pin, which
 * reads `routes.ts` and asserts each pattern still matches its intended detail
 * AND no other. The real fix is a machine-readable `Problem.code`, confirmed as
 * its own cross-cutting story (ADR-0093 D10) and deliberately NOT minted inside
 * a UI ticket; when it lands, this table and that pin both go away.
 *
 * Exported ONLY for that cross-product test: it needs each pattern
 * individually, because `classifyAppendConflict` returns the first match and so
 * cannot reveal an overlapping pair.
 */
export const CONFLICT_PATTERNS: ReadonlyArray<readonly [AppendLinesConflict, RegExp]> = [
  ['line-already-present', /already on this rfq/i],
  ['quote-in-progress', /specialist is pricing/i],
  ['lines-not-appendable', /no longer be added/i],
  ['stale', /changed concurrently/i],
  ['line-not-configured', /must be configured before it can be added/i],
]

/**
 * The `quote-in-progress` message the customer sees. Deliberately OUR copy,
 * not the server's `detail`: the server's phrasing ("A specialist is pricing
 * this RFQ right now…") is written for the operator-facing record, and this
 * is the one branch whose user-facing wording the ticket pins verbatim.
 */
export const ADD_PARTS_QUOTE_IN_PROGRESS_COPY =
  "This RFQ is being priced by our team; parts can't be added right now"

/** Classifies an append 409 from its `Problem.detail`. */
export function classifyAppendConflict(detail: string | undefined | null): AppendLinesConflict {
  if (!detail) return 'unknown'
  for (const [conflict, pattern] of CONFLICT_PATTERNS) {
    if (pattern.test(detail)) return conflict
  }
  return 'unknown'
}
